export const MAX_RECORDING_SECONDS = 60;
export const voiceSample = (key) => `/voices/${key}.mp3`;

export function newKey() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const supportsVoice = () => Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

export const pickMime = () =>
  ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";

// Markdown → plain words that read naturally aloud.
export function speakable(md) {
  return md
    .replace(/```[\s\S]*?(```|$)/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/^\s{0,3}(#{1,6}|>|[-*+])\s+/gm, "")
    .replace(/[*_~|#>]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Splits a streaming reply into speakable parts: a short first part so speech starts fast, then fuller sentences.
export function createChunker() {
  let buf = "";
  let count = 0;
  const take = (final) => {
    const out = [];
    while (buf) {
      const min = count === 0 ? 40 : 180;
      let cut = -1;
      const re = /[.!?…]+["')\]]*\s+|\n+/g;
      let m;
      while ((m = re.exec(buf))) {
        if (m.index + m[0].length >= min) { cut = m.index + m[0].length; break; }
      }
      if (cut < 0 && buf.length > 600) {
        const space = buf.lastIndexOf(" ", 600);
        cut = space > 0 ? space + 1 : 600;
      }
      if (cut < 0 && final) cut = buf.length;
      if (cut < 0) break;
      const piece = speakable(buf.slice(0, cut));
      buf = buf.slice(cut);
      if (piece) { out.push(piece); count += 1; }
    }
    return out;
  };
  return { push: (t) => { buf += t; return take(false); }, flush: () => take(true) };
}

// Per-step abort: a timeout (bump() restarts it) plus the session's own abort.
export function stepSignal(ms, parent) {
  const c = new AbortController();
  let timedOut = false;
  let t;
  const arm = () => { clearTimeout(t); t = setTimeout(() => { timedOut = true; c.abort(); }, ms); };
  const onParent = () => c.abort();
  parent?.addEventListener("abort", onParent, { once: true });
  arm();
  return { signal: c.signal, bump: arm, timedOut: () => timedOut, done: () => { clearTimeout(t); parent?.removeEventListener("abort", onParent); } };
}

export function micError(e) {
  const name = e?.name;
  if (name === "NotAllowedError" || name === "SecurityError")
    return { kind: "permission", message: "Microphone access is blocked for ChatSleek. Allow it in your browser's site settings (usually the lock icon next to the address), then tap to talk again." };
  if (name === "NotFoundError" || name === "OverconstrainedError")
    return { kind: "error", message: "No microphone was found. Connect one and try again." };
  if (name === "NotReadableError" || name === "AbortError")
    return { kind: "error", message: "Your microphone is busy in another app. Close that app and try again." };
  return { kind: "error", message: "ChatSleek couldn't start your microphone. Please try again." };
}

export function stepFailure(e, timedOut, retry) {
  if (e?.status === 402) return { kind: "credits", message: e.message };
  if (e?.status === 429) return { kind: "error", message: e.message };
  if (timedOut) return { kind: "error", message: "That took too long — your connection may be slow. Nothing extra was charged.", retry };
  if (e?.status === 0 || e?.name === "AbortError" || e?.name === "TypeError")
    return { kind: "error", message: "Connection lost. Check your internet and try again. Nothing extra was charged.", retry };
  return { kind: "error", message: e?.message || "Something went wrong. Please try again.", retry };
}

export const EMPTY_USAGE = { listenSeconds: 0, listenCredits: 0, speakChars: 0, speakCredits: 0, replyCredits: 0 };

export const formatSeconds = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
